import React from "react";
import { Activity, Stethoscope, FlaskConical, ArrowUpRight } from "lucide-react";

const POINTS = [
  {
    icon: Activity,
    label: "Monitor & Adapt",
    title: "Every program watches your data — and changes with it.",
    body: "Sleep, recovery, training, stress, nutrition. Your plan shifts week by week as your wearable signals shift. Never stale.",
    img: "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/mn0remsp_dashboard.png",
    alt: "Wearable, dashboard and lab results syncing in one connected view",
  },
  {
    icon: Stethoscope,
    label: "Medical Referral",
    title: "Need medical care? We connect you to our licensed Integrated Medical team.",
    body: "Anything that requires medically-supervised care — hormones, prescriptions, deeper diagnostics — is routed to our licensed Integrated Medical team. Wellness coaching and medical care, side by side.",
    img: "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/kz1ghvhg_n8Oo7oN4SQM7DJDjAayH3.png",
    alt: "Integrated Medical team reviewing a clinical dashboard with wellness program protocols",
    cta: {
      label: "Learn about Medical Care",
      href: "https://integratednycai.com/medical-care",
    },
  },
  {
    icon: FlaskConical,
    label: "Optional Home Test Kits",
    title: "Add labs, DNA, hormone, and microbiome testing to personalize your wellness plan.",
    body: "Optional advanced testing — labs, DNA analysis, microbiome, hormone panels — feeds the wellness plan with deeper context. Used to guide your program, not to diagnose.",
    img: "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/joxcfvqb_7LB9vdEZcaUK9HdXSlCNg.png",
    alt: "Home test kits — labs, DNA, hormone and microbiome panels for personalized wellness",
  },
];

export default function HowItAdaptsSection() {
  return (
    <section
      id="how-it-adapts"
      className="relative overflow-hidden border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-28"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-40" aria-hidden />
      <div
        className="pointer-events-none absolute -top-20 left-1/3 h-[420px] w-[420px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(37, 99, 235, 0.35), transparent 70%)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="iw-overline mb-3">§ 02 — How it actually works</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl">
              One intake.{" "}
              <span className="text-[color:var(--iw-blue)]">A wellness plan</span> that{" "}
              <span className="text-[color:var(--iw-accent-red)]">adapts</span> to your data.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-[color:var(--iw-body)]">
              You start with one intake. You pick the program that fits. From there, your plan keeps adjusting — guided by your wearable data, and sharpened by optional home test kits.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-7">
          {POINTS.map((p, idx) => (
            <article
              key={p.label}
              className="iw-scan-sweep group relative flex flex-col overflow-hidden border border-[color:var(--iw-border)] bg-white transition-all hover:-translate-y-1 hover:border-[color:var(--iw-blue)]/60 hover:shadow-[0_28px_60px_-22px_rgba(17,41,107,0.45)]"
            >
              {/* Image hero */}
              <div className="relative aspect-[5/4] overflow-hidden bg-[color:var(--iw-bg)]">
                <img
                  src={p.img}
                  alt={p.alt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#11296B]/70 via-[#11296B]/15 to-transparent" />

                {/* Corner brackets */}
                <span className="pointer-events-none absolute left-3 top-3 h-3 w-3 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
                <span className="pointer-events-none absolute right-3 top-3 h-3 w-3 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
                <span className="pointer-events-none absolute left-3 bottom-3 h-3 w-3 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
                <span className="pointer-events-none absolute right-3 bottom-3 h-3 w-3 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />

                {/* Step badge */}
                <div className="absolute left-4 top-4 inline-flex items-center gap-2 border border-white/50 bg-[color:var(--iw-primary)]/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white backdrop-blur">
                  <span className="text-[color:var(--iw-accent-red)]">●</span> Step 0{idx + 1}
                </div>

                {/* Icon + label overlay */}
                <div className="absolute inset-x-4 bottom-4 flex items-center gap-3">
                  <span className="inline-grid h-11 w-11 flex-shrink-0 place-items-center border border-[color:var(--iw-blue-bright)]/70 bg-white/95 text-[color:var(--iw-blue)] backdrop-blur">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white">
                    {p.label}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col p-6 sm:p-7">
                <h3 className="font-display text-xl font-semibold leading-tight tracking-tight text-[color:var(--iw-primary)] sm:text-2xl">
                  {p.title}
                </h3>
                <p className="mt-4 flex-1 text-base leading-relaxed text-[color:var(--iw-body)]">
                  {p.body}
                </p>
                {p.cta && (
                  <a
                    href={p.cta.href}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex w-fit items-center gap-2 border-b border-[color:var(--iw-accent-red)] pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--iw-primary)] transition-colors hover:text-[color:var(--iw-accent-red)]"
                  >
                    {p.cta.label} <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>

      </div>
    </section>
  );
}
